import { Component, type FormEvent } from "react";
import { pick } from "ramda";
import { observer } from "mobx-react";
import { action, computed, makeObservable, observable, when } from "mobx";
import { type LoggedInUserUpdate, type UserUpdate, type User } from "../../api";
import { createUuid } from "../../utils/uuid";
import { doubleBindNullableString } from "../../utils/double-bind";
import { store } from "../../store";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { ErrorText } from "@/components/ui/error-text";
import { FormSubmitButton } from "@/components/ui/form-submit-button";

export interface FormUpdatePasswordProps {
    readonly onUpdate?: (entity: User) => void | Promise<void>;
}

@observer
export class FormUpdatePassword extends Component<FormUpdatePasswordProps> {
    @observable public accessor password: string = "";
    @observable public accessor passwordRepeat: string = "";

    // Properties that aren't used during render and don't need to be observable
    private validationId = createUuid();

    constructor(props: FormUpdatePasswordProps) {
        super(props);
        makeObservable(this);
    }

    async componentDidMount(): Promise<void> {
        // Don't initialize if we don't have props yet.
        // This will otherwise fail.
        if (!this.props) {
            return;
        }

        await when(() => this.user !== undefined);
        store.repositoryUsers.validation.updateLoggedInUser.initializeModel(
            this.validationId,
            this.userUpdate,
        );
    }

    @computed private get user(): User | undefined {
        if (this.userId === undefined) {
            return;
        }
        return store.repositoryUsers.mutableCopyById(FormUpdatePassword.name, this.userId);
    }

    @computed private get userId(): string | undefined {
        return store.serviceOwnUser.user?.id;
    }

    @action.bound private triggerValidation(): void {
        if (this.userId === undefined) {
            return;
        }
        const validation = store.repositoryUsers.validation.updateLoggedInUser;
        if (!validation.isInitialized(this.validationId)) {
            validation.initializeModel(this.validationId, this.userUpdate);
        }
        validation.updateModel(this.validationId, this.userUpdate);
    }

    public componentWillUnmount(): void {
        if (this.userId === undefined) {
            return;
        }
        store.repositoryUsers.discardMutableCopy(FormUpdatePassword.name, this.userId);
    }

    @computed private get userUpdate(): LoggedInUserUpdate {
        const userUpdate: UserUpdate = pick(["password"], this.user);

        // The password cannot exist on the original [User] object.
        // Hence we save it on the form and manually set it the [LoggedInUserUpdate].
        if (this.password) {
            userUpdate.password = this.password;
        }

        return userUpdate;
    }

    @action.bound private reset(): void {
        this.password = "";
        this.passwordRepeat = "";
    }

    @action.bound
    private async updateLoggedInUser(evt: FormEvent): Promise<void> {
        if (this.userId === undefined) {
            return;
        }
        evt.preventDefault();
        const user = await store.repositoryUsers.updateLoggedInUser(this.userUpdate);
        this.props.onUpdate && this.props.onUpdate(user);
        this.reset();
    }

    @computed private get passwordRepeatValid(): boolean {
        return this.password === this.passwordRepeat;
    }

    @computed private get passwordRepeatErrorMessage(): string | undefined {
        if (this.passwordRepeatValid) {
            return;
        }
        return store.i18n.t("formCreateUser.passwordRepeatInvalid");
    }

    public onFormatDate = (date?: Date): string => {
        return store.i18n.formatDateOnly(date!);
    };

    public render(): JSX.Element {
        if (this.user === undefined) {
            return <></>;
        }

        const passwordErrorMessage = store.i18n.formatFieldValidationState(
            store.repositoryUsers.validation.updateLoggedInUser.getFieldValidationState(
                this.validationId,
                "password",
            ),
        );

        return (
            <form onSubmit={this.updateLoggedInUser} className="my-2 flex flex-col gap-2">
                <Label htmlFor="password">{store.i18n.t("formUpdateUser.password.label")}</Label>
                <div className="max-w-80">
                    <PasswordInput
                        {...doubleBindNullableString<FormUpdatePassword>(
                            this,
                            "password",
                            this.triggerValidation,
                        )}
                        errored={passwordErrorMessage != null}
                    />
                </div>
                {passwordErrorMessage && <ErrorText>{passwordErrorMessage}</ErrorText>}
                <Label htmlFor="password" className="mt-2">
                    {store.i18n.t("formUpdateUser.passwordRepeat.label")}
                </Label>
                <div className="max-w-80">
                    <PasswordInput
                        {...doubleBindNullableString<FormUpdatePassword>(this, "passwordRepeat")}
                        errored={this.passwordRepeatErrorMessage != null}
                    />
                </div>
                {this.passwordRepeatErrorMessage && (
                    <ErrorText>{this.passwordRepeatErrorMessage}</ErrorText>
                )}
                <div>
                    <FormSubmitButton
                        variant="secondary"
                        validationId={this.validationId}
                        validation={store.repositoryUsers.validation.updateLoggedInUser}
                        additionalCondition={this.passwordRepeatValid}
                    >
                        {store.i18n.t("formUpdateUser.submit.text")}
                    </FormSubmitButton>
                </div>
            </form>
        );
    }
}
